import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '../../components/Layout';
import { CheckCircleIcon, ChevronRightIcon, ChevronLeftIcon, FileTextIcon, UploadIcon, InfoIcon, BookOpenIcon, BuildingIcon, ClipboardIcon, CheckIcon } from 'lucide-react';
const ExemptionWizard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    programId: '',
    programName: '',
    university: '',
    justification: '',
    documents: [],
    agreed: false
  });
  // Parse program ID from URL query parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const programId = params.get('programId');
    if (programId) {
      // In a real app, you would fetch program details based on the ID
      // For this demo, we'll use sample data
      const programDetails = {
        id: programId,
        name: 'Master of Business Administration',
        university: 'UAE Business School',
        fieldOfStudy: 'Business Administration',
        exemptionReason: 'Field of study not aligned with national priorities'
      };
      setFormData({
        ...formData,
        programId: programDetails.id,
        programName: programDetails.name,
        university: programDetails.university,
        fieldOfStudy: programDetails.fieldOfStudy,
        exemptionReason: programDetails.exemptionReason
      });
    }
  }, [location]);
  const totalSteps = 5;
  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };
  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };
  const handleInputChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  const handleFileUpload = e => {
    const files = Array.from(e.target.files);
    setFormData({
      ...formData,
      documents: [...formData.documents, ...files]
    });
  };
  const handleRemoveDocument = index => {
    const updatedDocuments = [...formData.documents];
    updatedDocuments.splice(index, 1);
    setFormData({
      ...formData,
      documents: updatedDocuments
    });
  };
  const handleSubmit = () => {
    // In a real app, you would submit the form data to an API
    // For this demo, we'll just navigate to a success page
    navigate('/student/applications-and-scholarships?tab=exemptions');
  };
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6" data-id="element-4137">
            <div className="max-w-2xl mx-auto" data-id="element-4138">
              <h2 className="text-xl font-semibold text-gray-800 mb-4" data-id="element-4139">
                About Exemption Requests
              </h2>
              <div className="bg-primary-50 border border-primary-100 rounded-md p-4 mb-6" data-id="element-4140">
                <div className="flex" data-id="element-4141">
                  <InfoIcon className="w-5 h-5 text-primary-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-4142" />
                  <div data-id="element-4143">
                    <p className="text-sm text-primary-800 mb-2 font-medium" data-id="element-4144">
                      Why do I need an exemption?
                    </p>
                    <p className="text-sm text-primary-700" data-id="element-4145">
                      This program requires an exemption because it falls
                      outside the national priority fields of study. The
                      exemption process involves review by both the Ministry of
                      Higher Education and Scientific Research (MoHESR) and the
                      Executive Human Capability Development Council (EHCDC).
                    </p>
                  </div>
                </div>
              </div>
              <div className="space-y-4 mb-6" data-id="element-4146">
                <div className="flex items-start" data-id="element-4147">
                  <div className="flex-shrink-0 mt-1" data-id="element-4148">
                    <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center text-primary-600" data-id="element-4149">
                      1
                    </div>
                  </div>
                  <div className="ml-3" data-id="element-4150">
                    <h3 className="font-medium text-gray-800" data-id="element-4151">
                      Submit Request
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-4152">
                      Complete this form with your program details,
                      justification, and supporting documents.
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-4153">
                  <div className="flex-shrink-0 mt-1" data-id="element-4154">
                    <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center text-primary-600" data-id="element-4155">
                      2
                    </div>
                  </div>
                  <div className="ml-3" data-id="element-4156">
                    <h3 className="font-medium text-gray-800" data-id="element-4157">MoHESR Review</h3>
                    <p className="text-sm text-gray-600" data-id="element-4158">
                      The Ministry of Higher Education will review your request
                      and forward it to EHCDC.
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-4159">
                  <div className="flex-shrink-0 mt-1" data-id="element-4160">
                    <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center text-primary-600" data-id="element-4161">
                      3
                    </div>
                  </div>
                  <div className="ml-3" data-id="element-4162">
                    <h3 className="font-medium text-gray-800" data-id="element-4163">
                      EHCDC Decision
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-4164">
                      The Executive Human Capability Development Council will
                      make the final decision.
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-4165">
                  <div className="flex-shrink-0 mt-1" data-id="element-4166">
                    <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center text-primary-600" data-id="element-4167">
                      4
                    </div>
                  </div>
                  <div className="ml-3" data-id="element-4168">
                    <h3 className="font-medium text-gray-800" data-id="element-4169">
                      Decision Notification
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-4170">
                      You'll receive a notification with the decision and next
                      steps.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-yellow-50 border border-yellow-100 rounded-md p-4 mb-6" data-id="element-4171">
                <div className="flex" data-id="element-4172">
                  <InfoIcon className="w-5 h-5 text-yellow-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-4173" />
                  <div data-id="element-4174">
                    <p className="text-sm text-yellow-800 mb-1 font-medium" data-id="element-4175">
                      Important Note
                    </p>
                    <p className="text-sm text-yellow-700" data-id="element-4176">
                      The exemption process typically takes 2-4 weeks. You
                      cannot proceed with your application until your exemption
                      is approved.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>;
      case 2:
        return <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6" data-id="element-4177">
            <div className="max-w-2xl mx-auto" data-id="element-4178">
              <h2 className="text-xl font-semibold text-gray-800 mb-4" data-id="element-4179">
                Program Details
              </h2>
              <div className="space-y-6 mb-6" data-id="element-4180">
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-4181">
                  <div className="flex items-start" data-id="element-4182">
                    <BookOpenIcon className="w-5 h-5 text-gray-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-4183" />
                    <div data-id="element-4184">
                      <h3 className="font-medium text-gray-800" data-id="element-4185">
                        {formData.programName || 'Program Name'}
                      </h3>
                      <div className="mt-1 flex items-center text-sm text-gray-600" data-id="element-4186">
                        <BuildingIcon className="w-4 h-4 mr-1.5" data-id="element-4187" />
                        {formData.university || 'University Name'}
                      </div>
                    </div>
                  </div>
                </div>
                <div data-id="element-4188">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4189">
                    Field of Study
                  </label>
                  <div className="text-sm bg-gray-50 rounded-md p-3 border border-gray-200" data-id="element-4190">
                    {formData.fieldOfStudy || 'Business Administration'}
                  </div>
                </div>
                <div data-id="element-4191">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4192">
                    Reason for Exemption
                  </label>
                  <div className="text-sm bg-gray-50 rounded-md p-3 border border-gray-200" data-id="element-4193">
                    {formData.exemptionReason || 'Field of study not aligned with national priorities'}
                  </div>
                </div>
              </div>
              <div className="bg-primary-50 border border-primary-100 rounded-md p-4" data-id="element-4194">
                <div className="flex" data-id="element-4195">
                  <InfoIcon className="w-5 h-5 text-primary-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-4196" />
                  <p className="text-sm text-primary-700" data-id="element-4197">
                    These program details have been pre-filled based on your
                    selection. Please continue to provide your justification and
                    supporting documents.
                  </p>
                </div>
              </div>
            </div>
          </div>;
      case 3:
        return <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6" data-id="element-4198">
            <div className="max-w-2xl mx-auto" data-id="element-4199">
              <h2 className="text-xl font-semibold text-gray-800 mb-4" data-id="element-4200">
                Upload Supporting Documents
              </h2>
              <p className="text-gray-600 mb-6" data-id="element-4201">
                Please upload the following supporting documents to strengthen
                your exemption request:
              </p>
              <div className="space-y-6 mb-6" data-id="element-4202">
                <div data-id="element-4203">
                  <h3 className="text-sm font-medium text-gray-700 mb-2" data-id="element-4204">
                    Required Documents
                  </h3>
                  <ul className="space-y-2 text-sm text-gray-600 mb-4" data-id="element-4205">
                    <li className="flex items-center" data-id="element-4206">
                      <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2" data-id="element-4207" />
                      Admission letter or offer from the university
                    </li>
                    <li className="flex items-center" data-id="element-4208">
                      <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2" data-id="element-4209" />
                      Program curriculum or course outline
                    </li>
                    <li className="flex items-center" data-id="element-4210">
                      <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2" data-id="element-4211" />
                      Your updated CV/resume
                    </li>
                  </ul>
                </div>
                <div data-id="element-4212">
                  <h3 className="text-sm font-medium text-gray-700 mb-2" data-id="element-4213">
                    Additional Documents (Optional)
                  </h3>
                  <ul className="space-y-2 text-sm text-gray-600 mb-4" data-id="element-4214">
                    <li className="flex items-center" data-id="element-4215">
                      <CheckCircleIcon className="w-4 h-4 text-gray-300 mr-2" data-id="element-4216" />
                      Employer support letter
                    </li>
                    <li className="flex items-center" data-id="element-4217">
                      <CheckCircleIcon className="w-4 h-4 text-gray-300 mr-2" data-id="element-4218" />
                      Relevant certificates or qualifications
                    </li>
                    <li className="flex items-center" data-id="element-4219">
                      <CheckCircleIcon className="w-4 h-4 text-gray-300 mr-2" data-id="element-4220" />
                      Any other supporting evidence
                    </li>
                  </ul>
                </div>
                <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center" data-id="element-4221">
                  <UploadIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" data-id="element-4222" />
                  <p className="text-sm text-gray-600 mb-1" data-id="element-4223">
                    Drag and drop files here
                  </p>
                  <p className="text-xs text-gray-500 mb-3" data-id="element-4224">or</p>
                  <label className="h-10 px-4 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors inline-flex items-center cursor-pointer" data-id="element-4225">
                    Browse Files
                    <input type="file" multiple className="hidden" onChange={handleFileUpload} data-id="element-4226" />
                  </label>
                  <p className="text-xs text-gray-500 mt-3" data-id="element-4227">
                    Supported formats: PDF, DOC, DOCX, JPG, PNG (max 10MB each)
                  </p>
                </div>
                {formData.documents.length > 0 && <div data-id="element-4228">
                    <h3 className="text-sm font-medium text-gray-700 mb-2" data-id="element-4229">
                      Uploaded Documents ({formData.documents.length})
                    </h3>
                    <div className="space-y-2" data-id="element-4230">
                      {formData.documents.map((doc, index) => <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-md border border-gray-200" data-id="element-4231">
                          <div className="flex items-center" data-id="element-4232">
                            <FileTextIcon className="w-4 h-4 text-gray-500 mr-2" data-id="element-4233" />
                            <span className="text-sm text-gray-700" data-id="element-4234">
                              {doc.name || `Document ${index + 1}`}
                            </span>
                          </div>
                          <button onClick={() => handleRemoveDocument(index)} className="text-red-500 hover:text-red-700 text-sm" data-id="element-4235">
                            Remove
                          </button>
                        </div>)}
                    </div>
                  </div>}
              </div>
            </div>
          </div>;
      case 4:
        return <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6" data-id="element-4236">
            <div className="max-w-2xl mx-auto" data-id="element-4237">
              <h2 className="text-xl font-semibold text-gray-800 mb-4" data-id="element-4238">
                Justification
              </h2>
              <p className="text-gray-600 mb-6" data-id="element-4239">
                Please provide a detailed justification for your exemption
                request. Explain why this program is essential for your academic
                and career goals.
              </p>
              <div className="space-y-6 mb-6" data-id="element-4240">
                <div data-id="element-4241">
                  <label htmlFor="justification" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4242">
                    Your Justification
                  </label>
                  <textarea id="justification" name="justification" rows={8} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm" placeholder="Explain why you need to study this program and how it aligns with your career goals and the country's development needs..." value={formData.justification} onChange={handleInputChange} data-id="element-4243"></textarea>
                  <p className="mt-1 text-xs text-gray-500" data-id="element-4244">
                    Minimum 200 characters. Be specific and provide concrete
                    examples.
                  </p>
                </div>
                <div className="bg-yellow-50 border border-yellow-100 rounded-md p-4" data-id="element-4245">
                  <div className="flex" data-id="element-4246">
                    <InfoIcon className="w-5 h-5 text-yellow-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-4247" />
                    <div data-id="element-4248">
                      <p className="text-sm text-yellow-800 font-medium mb-1" data-id="element-4249">
                        Tips for a Strong Justification
                      </p>
                      <ul className="list-disc pl-5 text-sm text-yellow-700 space-y-1" data-id="element-4250">
                        <li data-id="element-4251">
                          Explain how this program relates to your current work
                          or future career plans
                        </li>
                        <li data-id="element-4252">
                          Highlight any unique aspects of the program not
                          available in priority fields
                        </li>
                        <li data-id="element-4253">
                          Describe how your studies will contribute to the UAE's
                          development goals
                        </li>
                        <li data-id="element-4254">
                          Mention any employer support or industry demand for
                          these skills
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>;
      case 5:
        return <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6" data-id="element-4255">
            <div className="max-w-2xl mx-auto" data-id="element-4256">
              <h2 className="text-xl font-semibold text-gray-800 mb-4" data-id="element-4257">
                Review & Submit
              </h2>
              <p className="text-gray-600 mb-6" data-id="element-4258">
                Please review your exemption request details before submitting.
              </p>
              <div className="space-y-6 mb-6" data-id="element-4259">
                <div data-id="element-4260">
                  <h3 className="text-sm font-medium text-gray-700 mb-2" data-id="element-4261">
                    Program Details
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-4262">
                    <div className="grid grid-cols-2 gap-4" data-id="element-4263">
                      <div data-id="element-4264">
                        <p className="text-xs text-gray-500" data-id="element-4265">Program</p>
                        <p className="text-sm text-gray-800 font-medium" data-id="element-4266">
                          {formData.programName || 'Master of Business Administration'}
                        </p>
                      </div>
                      <div data-id="element-4267">
                        <p className="text-xs text-gray-500" data-id="element-4268">University</p>
                        <p className="text-sm text-gray-800 font-medium" data-id="element-4269">
                          {formData.university || 'UAE Business School'}
                        </p>
                      </div>
                      <div data-id="element-4270">
                        <p className="text-xs text-gray-500" data-id="element-4271">Field of Study</p>
                        <p className="text-sm text-gray-800 font-medium" data-id="element-4272">
                          {formData.fieldOfStudy || 'Business Administration'}
                        </p>
                      </div>
                      <div data-id="element-4273">
                        <p className="text-xs text-gray-500" data-id="element-4274">
                          Exemption Reason
                        </p>
                        <p className="text-sm text-gray-800 font-medium" data-id="element-4275">
                          {formData.exemptionReason || 'Field of study not aligned with national priorities'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div data-id="element-4276">
                  <h3 className="text-sm font-medium text-gray-700 mb-2" data-id="element-4277">
                    Supporting Documents
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-4278">
                    {formData.documents.length > 0 ? <ul className="space-y-2" data-id="element-4279">
                        {formData.documents.map((doc, index) => <li key={index} className="flex items-center" data-id="element-4280">
                            <FileTextIcon className="w-4 h-4 text-gray-500 mr-2" data-id="element-4281" />
                            <span className="text-sm text-gray-700" data-id="element-4282">
                              {doc.name || `Document ${index + 1}`}
                            </span>
                          </li>)}
                      </ul> : <p className="text-sm text-gray-500" data-id="element-4283">
                        No documents uploaded
                      </p>}
                  </div>
                </div>
                <div data-id="element-4284">
                  <h3 className="text-sm font-medium text-gray-700 mb-2" data-id="element-4285">
                    Justification
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-4286">
                    <p className="text-sm text-gray-700 whitespace-pre-line" data-id="element-4287">
                      {formData.justification || 'No justification provided'}
                    </p>
                  </div>
                </div>
                <div className="pt-4" data-id="element-4288">
                  <label className="flex items-start cursor-pointer" data-id="element-4289">
                    <input type="checkbox" name="agreed" checked={formData.agreed} onChange={handleInputChange} className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" data-id="element-4290" />
                    <span className="ml-2 text-sm text-gray-700" data-id="element-4291">
                      I confirm that all information provided is accurate and
                      complete. I understand that providing false information
                      may result in the rejection of my exemption request and
                      may affect future applications.
                    </span>
                  </label>
                </div>
              </div>
              <div className="bg-primary-50 border border-primary-100 rounded-md p-4" data-id="element-4292">
                <div className="flex" data-id="element-4293">
                  <InfoIcon className="w-5 h-5 text-primary-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-4294" />
                  <p className="text-sm text-primary-700" data-id="element-4295">
                    Once submitted, your exemption request will be reviewed by
                    the Ministry of Higher Education and Scientific Research
                    (MoHESR) and then by the Executive Human Capability
                    Development Council (EHCDC). This process typically takes
                    2-4 weeks.
                  </p>
                </div>
              </div>
            </div>
          </div>;
      default:
        return <div data-id="element-4296">Unknown step</div>;
    }
  };
  return <Layout title="Request Exemption" persona="Student" data-id="element-4297">
      <div className="max-w-5xl mx-auto" data-id="element-4298">
        {/* Progress bar */}
        <div className="mb-8" data-id="element-4299">
          <div className="flex items-center justify-between mb-2" data-id="element-4300">
            {['Introduction', 'Program', 'Documents', 'Justification', 'Review'].map((step, index) => <div key={index} className="flex flex-col items-center" data-id="element-4301">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${index + 1 === currentStep ? 'bg-primary-500 text-white' : index + 1 < currentStep ? 'bg-primary-100 text-primary-600' : 'bg-gray-100 text-gray-500'}`} data-id="element-4302">
                  {index + 1 < currentStep ? <CheckIcon className="w-5 h-5" data-id="element-4303" /> : <span data-id="element-4304">{index + 1}</span>}
                </div>
                <span className={`text-xs mt-1 ${index + 1 === currentStep ? 'text-primary-600 font-medium' : 'text-gray-500'}`} data-id="element-4305">
                  {step}
                </span>
              </div>)}
          </div>
          <div className="relative h-2 bg-gray-100 rounded-full" data-id="element-4306">
            <div className="absolute h-2 bg-primary-500 rounded-full" style={{
            width: `${(currentStep - 1) / (totalSteps - 1) * 100}%`
          }} data-id="element-4307"></div>
          </div>
        </div>
        {/* Step content */}
        {renderStep()}
        {/* Navigation buttons */}
        <div className="mt-8 flex justify-between" data-id="element-4308">
          {currentStep > 1 ? <button onClick={prevStep} className="h-10 px-4 bg-white text-gray-700 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors flex items-center" data-id="element-4309">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-4310" />
              Back
            </button> : <div data-id="element-4311"></div>}
          {currentStep < totalSteps ? <button onClick={nextStep} className="h-10 px-4 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors flex items-center" data-id="element-4312">
              Next
              <ChevronRightIcon className="w-4 h-4 ml-1" data-id="element-4313" />
            </button> : <button onClick={handleSubmit} disabled={!formData.agreed} className={`h-10 px-4 rounded-lg text-sm font-medium transition-colors flex items-center ${formData.agreed ? 'bg-primary-500 text-white hover:bg-primary-600' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`} data-id="element-4314">
              Submit Request
              <ChevronRightIcon className="w-4 h-4 ml-1" data-id="element-4315" />
            </button>}
        </div>
      </div>
    </Layout>;
};
export default ExemptionWizard;